import { useEffect, useMemo, useRef, useState } from 'react';

import { deleteAttendEvent } from '@src/api/attend';
import { GetAttendEventsResponse } from '@src/api/attend/getAttendEvents';
import { CreateAttendEvent } from '@src/components/Modals/CreateAttendEventModal/type';
import colors from '@src/constants/colors';
import { closeModal, openModal } from '@src/reduce/modals';
import { Get } from '@src/types/util';
import downloadBlob from '@src/utils/downloadBlob';
import { Modal, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useDispatch } from 'react-redux';

import useGetAttendEvents from '@hooks/queries/attend/useGetAttendEvents';

import { off, on } from '@utils/globalEvents';

import Text from '@components/Text';

import * as S from './styled';
import type * as T from './type';

function AttendEventTable() {
  const PAGE_SIZE: number = 8;

  const dispatch = useDispatch();

  const [page, setPage] = useState<number>(1);

  const { data: AttendEvents, total, isLoading, refetch } = useGetAttendEvents();

  const dataSource: T.AttendEventTableDataSource = useMemo(() => {
    return AttendEvents.map(v => ({ ...v, key: v.id }));
  }, [AttendEvents]);

  const openAttendManageEventModal = (groupEventId: number, title: string) => {
    dispatch(openModal({ name: 'attendManageEventModal', props: { groupEventId, title } }));
  };

  const openDelegateManageEventModal = (groupEventId: number, title: string) => {
    dispatch(openModal({ name: 'delegateManageEventModal', props: { groupEventId, title } }));
  };

  const openConfirmAlertModal = (groupEventId: number) => {
    dispatch(
      openModal({
        name: 'confirmAlertModal',
        props: {
          text: '삭제하시겠습니까?',
          isConfirm: true,
          close: deleteClose,
          groupEventId: groupEventId,
        },
      }),
    );
  };

  const openCreateAttendEventModal = (
    event: Get<GetAttendEventsResponse['rows']>,
    isEditable?: boolean,
  ) => {
    const initialFormData: CreateAttendEvent = {
      title: event.title,
      content: event.content,
      date: event.date,
      endDate: event.endDate,
      address: event.address,
      detailAddress: event.detailAddress,
      maxCount: event.maxCount,
      host: event.host,
      manager: event.manager,
      location: {
        coordinates: event.location.coordinates || [],
      },
      blockNotSincereMember: !!event.blockNotSincereMember,
      delegable: !!event.delegable,
    };

    dispatch(
      openModal({
        name: 'createAttendEventModal',
        props: {
          initialFormData: {
            ...initialFormData,
            id: event.id,
          },
          isEditable,
        },
      }),
    );
  };

  const deleteClose = () => {
    dispatch(closeModal('confirmAlertModal'));
  };

  useEffect(() => {
    on('SUCCESS_UPDATE_ATTEND_EVENT', refetch);

    return () => {
      off('SUCCESS_UPDATE_ATTEND_EVENT', refetch);
    };
  }, []);

  const columns = useMemo<ColumnsType<T.AttendEventTableData>>(() => {
    const columnsValue: ColumnsType<T.AttendEventTableData> = [
      {
        key: 'id',
        dataIndex: 'id',
        width: '5%',
        title: '번호',
      },
      {
        key: 'title',
        dataIndex: 'title',
        title: '행사명',
        width: '21%',
        render: title => {
          return <>{title}</>;
        },
      },
      {
        key: 'date',
        dataIndex: 'date',
        title: '시작일시',
        width: '12%',
        render: date => {
          const dateYMD = dayjs(date).format('YYYY.MM.DD HH:mm');
          return <>{dateYMD}</>;
        },
      },
      {
        key: 'endDate',
        dataIndex: 'endDate',
        title: '종료일시',
        width: '12%',
        render: endDate => {
          const endDateYMD = dayjs(endDate).format('YYYY.MM.DD HH:mm');
          return <>{endDateYMD}</>;
        },
      },
      {
        key: 'createdAt',
        dataIndex: 'createdAt',
        title: '등록일',
        width: '10%',
        render: createdAt => {
          const createdAtYMD = dayjs(createdAt).format('YYYY.MM.DD');
          return <>{createdAtYMD}</>;
        },
      },
      {
        key: 'content',
        dataIndex: 'content',
        title: '내용',
        width: '10%',
        render: (_, record) => {
          return (
            <Text
              underline={true}
              cursor='pointer'
              onClick={() => openCreateAttendEventModal(record)}>
              상세보기
            </Text>
          );
        },
      },
      {
        key: 'qrcode',
        title: 'QR코드',
        width: '10%',
        render: (_, record) => {
          const href = `https://github-readme-awesome.vercel.app/api/image?url=kcplaa${
            /localhost|-stg/.test(window.location.origin) ? '-stage' : ''
          }://groupEvent/${record.id}&isDownload=true`;
          return (
            <Text underline={true} cursor='pointer'>
              <S.Link href={href}>QR코드 다운</S.Link>
            </Text>
          );
        },
      },
      {
        key: 'manage',
        title: '관리',
        width: '10%',
        render: (_, record) => {
          return (
            <>
              <Text
                underline={true}
                cursor='pointer'
                onClick={() => openAttendManageEventModal(record.id, record.title)}>
                출석자관리
              </Text>
              <Text
                underline={true}
                cursor={record.delegable ? 'pointer' : undefined}
                onClick={
                  record.delegable
                    ? () => openDelegateManageEventModal(record.id, record.title)
                    : undefined
                }
                color={record.delegable ? undefined : colors.GRAY_BRIGHT_1}>
                위임자관리
              </Text>
            </>
          );
        },
      },
      {
        key: 'actions',
        title: '',
        width: '10%',
        render: (_, record) => {
          return (
            <div style={{ display: 'flex', flexDirection: 'row', gap: 16 }}>
              <Text
                underline={true}
                cursor='pointer'
                onClick={() => openCreateAttendEventModal(record, true)}>
                수정
              </Text>
              <Text
                underline={true}
                cursor='pointer'
                onClick={() => openConfirmAlertModal(record.id)}>
                삭제
              </Text>
            </div>
          );
        },
      },
    ];

    return columnsValue;
  }, [AttendEvents]);

  return (
    <Table
      columns={columns}
      dataSource={dataSource}
      loading={isLoading}
      pagination={{
        total,
        position: ['bottomCenter'],
        pageSize: PAGE_SIZE,
        showSizeChanger: false,
        onChange: setPage,
      }}
    />
  );
}

export default AttendEventTable;
